/**
 * One price, one location. The INR/USD toggle swaps which amount leads; the
 * other stays in the line beneath. The default follows the browser language
 * (Indian locales lead with rupees) and every switch is a GA4 event.
 *
 * Under the price (Anand, 13 Oct 26), two compact lines and nothing more:
 * the yearly plan, highlighted ("Pay yearly and get 2 months free", the year
 * at ten months' list price in the active currency), and the agency volume
 * tiers. Billing charges the same: the yearly product is ten months' price
 * (supabase/functions/_shared/gbp-agent-billing-interval.ts; the guard
 * tests/agent/unit/billing-yearly.test.ts holds the two together, since the
 * app may not import the edge code).
 */

import { useEffect, useState, type CSSProperties } from 'react';
import { trackGoogleEvent } from '@/utils/tracking';
import { TrialLink } from './Bits';

export type Currency = 'INR' | 'USD';

export const PRICE: Record<Currency, string> = { INR: 'Rs 2,499', USD: '$24.99' };
/** The same amounts as bare numbers, for the Offer records in the page head (LandingHead). */
export const PRICE_AMOUNT: Record<Currency, string> = { INR: '2499', USD: '24.99' };
/** What follows the price: GST is added in India (Anand, 10 Oct 26: GST extra, always), nothing elsewhere. */
export const PRICE_NOTE: Record<Currency, string> = { INR: 'plus 18% GST', USD: '' };
/** The same prices in minor units (paise, cents): what the yearly price is worked from. */
export const PRICE_MINOR: Record<Currency, number> = { INR: 249900, USD: 2499 };

/** Anand, 13 Oct 26: "2 months off on annual subscriptions". A year is paid as ten months. */
export const YEARLY_MONTHS_PAID = 10;
export const YEARLY_MONTHS_FREE = 12 - YEARLY_MONTHS_PAID;

/** "Rs 24,990" or "$249.90": the year at ten months' list price, written the way PRICE is. */
export function yearlyPrice(c: Currency): string {
  const amount = (PRICE_MINOR[c] * YEARLY_MONTHS_PAID) / 100;
  return c === 'INR' ? `Rs ${amount.toLocaleString('en-IN')}` : `$${amount.toFixed(2)}`;
}

/** The yearly amounts as bare numbers, for the Offer records in the page head (LandingHead). */
export const YEARLY_AMOUNT: Record<Currency, string> = { INR: '24990', USD: '249.90' };

/** The highlighted lead of the yearly line. */
export const YEARLY_LEAD = `Pay yearly and get ${YEARLY_MONTHS_FREE} months free`;

/** "Rs 24,990 a year plus GST" or "$249.90 a year": what follows the lead. */
export function yearlyWords(c: Currency): string {
  return `${yearlyPrice(c)} a year${c === 'INR' ? ' plus GST' : ''}`;
}

/**
 * The agency volume tiers in one compact line (Anand, 13 Oct 26: "up to 5
 * locations no discount, 6 to 10 10% off, 11 to 25 20% off, 26+ 30% off").
 */
export const TIERS_LINE = 'Agencies and multi-location: 6 to 10 locations 10% off, 11 to 25 20% off, 26 and more 30% off';

/** "Rs 2,499 plus 18% GST" or "$24.99": the price with its tax note. */
export function priceWords(c: Currency): string {
  return PRICE_NOTE[c] ? `${PRICE[c]} ${PRICE_NOTE[c]}` : PRICE[c];
}

/** Indian locales lead with rupees; everyone else with dollars. Browser only. */
export function detectCurrency(): Currency {
  return typeof navigator !== 'undefined' && /^en-IN|hi|mr|ta|te|bn|gu|kn|ml|pa/i.test(navigator.language) ? 'INR' : 'USD';
}

/** The three points under the price (exported for the Markdown renditions, scripts/landing/landing-md-data.ts). */
export const PRICE_POINTS = ['14 days free, one location, no card', 'Cancel any time', 'Pay by card at a secure checkout'];
const ORDER: Currency[] = ['INR', 'USD'];

/** The line over the price card when a variant gives none (exported for the Markdown renditions). */
export const PRICING_LINE = 'Free for 14 days. Pay only if you keep it.';

export default function Pricing({ line = PRICING_LINE }: { line?: string }) {
  // The first paint matches the approved page (rupees lead); the browser then
  // corrects it, so server and client render the same markup.
  const [currency, setCurrency] = useState<Currency>('INR');
  useEffect(() => {
    setCurrency(detectCurrency());
  }, []);
  const other: Currency = currency === 'INR' ? 'USD' : 'INR';

  const choose = (c: Currency) => {
    setCurrency(c);
    trackGoogleEvent('gbp_agent_currency_toggle', { currency: c });
  };

  return (
    <section className="sec" id="pricing">
      <div className="wrap price-wrap">
        <div>
          <h2 className="h2 rv">
            One price, <em>one location.</em>
          </h2>
          <p className="line rv" style={{ '--i': 1 } as CSSProperties}>
            {line}
          </p>
        </div>
        <div className="price ag-glass rv" style={{ '--i': 1 } as CSSProperties}>
          <span className="lbl">GBP Growth Agent</span>
          <div className="seg" data-pos={ORDER.indexOf(currency)} role="group" aria-label="Currency">
            <i className="seg-bg" aria-hidden="true" />
            {ORDER.map((c) => (
              <span key={c} className={currency === c ? 'on' : undefined} role="button" tabIndex={0} aria-pressed={currency === c} onClick={() => choose(c)} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && choose(c)}>
                {c}
              </span>
            ))}
          </div>
          <div className="amt">
            <b>{PRICE[currency]}</b>
            <span>
              per location per month{PRICE_NOTE[currency] ? ` ${PRICE_NOTE[currency]}` : ''}, or {priceWords(other)}
            </span>
          </div>
          <p className="yearly" data-testid="pricing-yearly">
            <b>{YEARLY_LEAD}:</b> {yearlyWords(currency)}
          </p>
          <p className="tiers" data-testid="pricing-tiers">
            {TIERS_LINE}
          </p>
          <ul>
            {PRICE_POINTS.map((p) => (
              <li key={p}>
                <svg viewBox="0 0 24 24">
                  <path d="M5 12l4.5 4.5L19 7" />
                </svg>
                {p}
              </li>
            ))}
          </ul>
          <TrialLink placement="pricing" className="btn btn-primary btn-lg" extra={{ currency }}>
            Start 14 day free trial
          </TrialLink>
        </div>
      </div>
    </section>
  );
}
